import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { VendorAPI } from '../lib/b2bApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Building2, FlaskConical, Pill, Package, Gift, CheckCircle, XCircle, Loader2, KeyRound, TrendingUp } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// MPIN Input component - 4 digit PIN
function MPINInput({ value, onChange, placeholder = "Enter 4-digit MPIN" }) {
  return (
    <Input
      type="password"
      inputMode="numeric"
      maxLength={4}
      pattern="[0-9]*"
      data-testid="vendor-mpin-input"
      placeholder={placeholder}
      value={value}
      onChange={(e) => {
        const val = e.target.value.replace(/\D/g, '').slice(0, 4);
        onChange(val);
      }}
      className="text-center tracking-[0.5em] text-lg font-mono"
    />
  );
}

export default function VendorLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showMRFields, setShowMRFields] = useState(false);
  const [referralStatus, setReferralStatus] = useState(null);
  const [referrerName, setReferrerName] = useState('');
  const [showForgotMPIN, setShowForgotMPIN] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotPhone, setForgotPhone] = useState('');
  const [forgotOTP, setForgotOTP] = useState('');
  const [newMPIN, setNewMPIN] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [testOTP, setTestOTP] = useState('');
  
  const [form, setForm] = useState({
    email: '', mpin: '', name: '', vendor_type: '',
    phone: '', address: '', gst_number: '', license_number: '', description: '',
    referral_code: '', vendor_category: '',
    mr_executive: { name: '', phone: '', email: '', territory: '' }
  });
  
  // Google OAuth completion state
  const [googleData, setGoogleData] = useState(null);

  // Handle Google OAuth callback
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      if (sessionId) {
        handleGoogleCallback(sessionId);
        window.history.replaceState(null, '', window.location.pathname);
      }
    }
  }, [location]);

  const handleGoogleCallback = async (sessionId) => {
    setGoogleLoading(true);
    try {
      const res = await VendorAPI.post(`/vendors/google-auth?session_id=${sessionId}`);
      
      if (res.data.is_new) {
        setGoogleData({
          email: res.data.google_email,
          name: res.data.google_name
        });
        setForm(prev => ({
          ...prev,
          email: res.data.google_email,
          name: res.data.google_name || ''
        }));
        setIsLogin(false);
        toast.info('Please complete your vendor registration');
      } else {
        localStorage.setItem('vendor_token', res.data.token);
        localStorage.setItem('vendor_user', JSON.stringify(res.data.vendor));
        toast.success('Welcome back!');
        const pendingRx = localStorage.getItem('pending_rx_barcode');
        if (pendingRx) {
          localStorage.removeItem('pending_rx_barcode');
          navigate(`/vendor/dashboard?rx=${pendingRx}`);
        } else {
          navigate('/vendor/dashboard');
        }
      }
    } catch (err) {
      const _d1 = err.response?.data?.detail; toast.error(typeof _d1 === 'string' ? _d1 : 'Google authentication failed');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/vendor/login';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const validateReferralCode = async (code) => {
    if (!code || code.length < 4) {
      setReferralStatus(null);
      setReferrerName('');
      return;
    }
    setReferralStatus('checking');
    try {
      const res = await axios.post(`${API_URL}/api/referrals/validate?code=${code}`);
      if (res.data.valid) {
        setReferralStatus('valid');
        setReferrerName(res.data.referrer_name);
      }
    } catch (err) {
      setReferralStatus('invalid');
      setReferrerName('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.email) {
      toast.error('Please enter your email');
      return;
    }
    
    if (isLogin && form.mpin.length !== 4) {
      toast.error('Please enter a 4-digit MPIN');
      return;
    }
    if (!isLogin && form.mpin.length !== 4) {
      toast.error('Please set a 4-digit MPIN');
      return;
    }
    
    setLoading(true);

    try {
      if (isLogin) {
        const res = await VendorAPI.post(`/vendors/login?email_or_phone=${encodeURIComponent(form.email)}&mpin=${encodeURIComponent(form.mpin)}`);
        localStorage.setItem('vendor_token', res.data.token);
        localStorage.setItem('vendor_user', JSON.stringify(res.data.vendor));
        toast.success('Welcome back!');
        const pendingRx2 = localStorage.getItem('pending_rx_barcode');
        if (pendingRx2) {
          localStorage.removeItem('pending_rx_barcode');
          navigate(`/vendor/dashboard?rx=${pendingRx2}`);
        } else {
          navigate('/vendor/dashboard');
        }
      } else {
        const registrationData = {
          name: form.name,
          email: form.email,
          mpin: form.mpin,
          vendor_type: form.vendor_type,
          phone: form.phone,
          address: form.address,
          gst_number: form.gst_number,
          license_number: form.license_number,
          description: form.description,
          referral_code: form.referral_code || '',
        };
        
        if (form.vendor_type === 'marketing' && form.vendor_category) {
          registrationData.vendor_category = form.vendor_category;
        }

        if (form.mr_executive.name || form.mr_executive.phone || form.mr_executive.email) {
          registrationData.mr_executive = form.mr_executive;
        }
        
        const res = await VendorAPI.post('/vendors/register', registrationData);
        toast.success('Registration successful! Please wait for admin approval.');
        setIsLogin(true);
        setGoogleData(null);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMessage = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : '';
      
      if (errorMessage.toLowerCase().includes('no account found')) {
        toast.error('No account found with this email or phone.');
      } else if (errorMessage.toLowerCase().includes('incorrect mpin')) {
        toast.error('Incorrect MPIN. Please try again.');
      } else if (errorMessage.toLowerCase().includes('reset your mpin')) {
        toast.error('Please reset your MPIN using the "Forgot MPIN" option.');
      } else if (errorMessage.toLowerCase().includes('pending') || 
                 errorMessage.toLowerCase().includes('approval')) {
        toast.error('Your account is pending admin approval.');
      } else {
        toast.error(errorMessage || 'Authentication failed. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotMPIN = async () => {
    if (!forgotEmail) {
      toast.error('Please enter your email');
      return;
    }
    setLoading(true);
    try {
      const res = await VendorAPI.post(`/vendors/forgot-mpin?email=${encodeURIComponent(forgotEmail)}${forgotPhone ? `&phone=${encodeURIComponent(forgotPhone)}` : ''}`);
      setOtpSent(true);
      setTestOTP(res.data.otp_for_testing || '');
      toast.success('OTP sent to your email & WhatsApp');
    } catch (err) {
      const _d2 = err.response?.data?.detail; toast.error(typeof _d2 === 'string' ? _d2 : 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResetMPIN = async () => {
    if (newMPIN.length !== 4) {
      toast.error('Please enter a 4-digit MPIN');
      return;
    }
    setLoading(true);
    try {
      await VendorAPI.post(`/vendors/reset-mpin?email=${encodeURIComponent(forgotEmail)}&otp=${forgotOTP}&new_mpin=${newMPIN}`);
      toast.success('MPIN reset successfully! Please login.');
      setShowForgotMPIN(false);
      setOtpSent(false);
      setForgotEmail('');
      setForgotOTP('');
      setNewMPIN('');
    } catch (err) {
      const _d3 = err.response?.data?.detail; toast.error(typeof _d3 === 'string' ? _d3 : 'Failed to reset MPIN');
    } finally {
      setLoading(false);
    }
  };

  const vendorTypes = [
    { value: 'lab', label: 'Pathology Lab', icon: FlaskConical, color: 'text-cyan-600' },
    { value: 'pharma', label: 'Pharmaceutical Company', icon: Pill, color: 'text-pink-600' },
    { value: 'chemist', label: 'Chemist / Pharmacy', icon: Pill, color: 'text-emerald-600' },
    { value: 'inventory', label: 'Medical Equipment Supplier', icon: Package, color: 'text-orange-600' },
    { value: 'marketing', label: 'Marketing Agency', icon: TrendingUp, color: 'text-violet-600' },
  ];

  if (googleLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-800 via-teal-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-white mx-auto mb-4" />
          <p className="text-white">Completing Google Sign In...</p>
        </div>
      </div>
    );
  }

  // Forgot MPIN Modal
  if (showForgotMPIN) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-800 via-teal-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white/95 backdrop-blur shadow-2xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-teal-600 to-emerald-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
              <KeyRound className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-800">Reset MPIN</CardTitle>
            <CardDescription>
              {!otpSent ? 'Enter your email to receive an OTP' : 'Enter OTP and your new 4-digit MPIN'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!otpSent ? (
              <>
                <div className="space-y-2">
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    placeholder="business@example.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    data-testid="vendor-forgot-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone Number <span className="text-muted-foreground text-xs">(for WhatsApp OTP)</span></Label>
                  <Input
                    type="tel"
                    placeholder="+91 9876543210"
                    value={forgotPhone}
                    onChange={(e) => setForgotPhone(e.target.value)}
                    data-testid="vendor-forgot-phone"
                  />
                </div>
                <Button
                  onClick={handleForgotMPIN}
                  className="w-full bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700"
                  disabled={loading}
                  data-testid="vendor-forgot-send-otp"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Send OTP
                </Button>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>OTP</Label>
                  <Input
                    type="text"
                    placeholder="6-digit OTP"
                    value={forgotOTP}
                    onChange={(e) => setForgotOTP(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="text-center tracking-widest"
                    maxLength={6}
                  />
                  {testOTP && <p className="text-xs text-slate-500">Test OTP: {testOTP}</p>}
                </div>
                <div className="space-y-2">
                  <Label>New MPIN (4 digits)</Label>
                  <MPINInput value={newMPIN} onChange={setNewMPIN} placeholder="Enter new MPIN" />
                </div>
                <Button
                  onClick={handleResetMPIN}
                  className="w-full bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700"
                  disabled={loading}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Reset MPIN
                </Button>
              </>
            )}
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => {
                setShowForgotMPIN(false);
                setOtpSent(false);
              }}
            >
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-800 via-teal-900 to-slate-900 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-grid-white/5 opacity-40"></div>

      <Card className="w-full max-w-lg relative z-10 bg-white/95 backdrop-blur shadow-2xl border-0">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-teal-600 to-emerald-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-800">
            {isLogin ? 'Vendor Portal' : 'Vendor Registration'}
          </CardTitle>
          <CardDescription className="text-slate-500">
            {isLogin ? 'Access your business dashboard' : 'Join the 1Clin marketplace'}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {isLogin && (
              <>
                {/* Google Sign In Button */}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-slate-300 hover:bg-slate-50"
                  onClick={handleGoogleLogin}
                  data-testid="google-login-btn"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Sign in with Google
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-slate-200" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-slate-500">Or continue with MPIN</span>
                  </div>
                </div>
              </>
            )}

            {!isLogin && (
              <>
                {googleData && (
                  <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-teal-800">
                      <CheckCircle className="w-4 h-4 inline mr-1" />
                      Signed in as <strong>{googleData.email}</strong>
                    </p>
                    <p className="text-xs text-teal-600 mt-1">Please complete your business profile</p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Business Type *</Label>
                  <Select value={form.vendor_type} onValueChange={(v) => setForm({ ...form, vendor_type: v })}>
                    <SelectTrigger data-testid="vendor-type-select">
                      <SelectValue placeholder="Select your business type" />
                    </SelectTrigger>
                    <SelectContent>
                      {vendorTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon className={`w-4 h-4 ${type.color}`} />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {form.vendor_type === 'marketing' && (
                  <div className="space-y-2">
                    <Label>Marketing Category *</Label>
                    <Select value={form.vendor_category} onValueChange={(v) => setForm({ ...form, vendor_category: v })}>
                      <SelectTrigger data-testid="vendor-category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="online_marketing">Online Marketing</SelectItem>
                        <SelectItem value="offline_marketing">Offline Marketing</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="name">Business Name *</Label>
                  <Input
                    id="name"
                    data-testid="vendor-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your business name"
                    required={!isLogin}
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                data-testid="vendor-email-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="business@example.com"
                required
                disabled={!!googleData}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="mpin" className="flex items-center gap-2">
                <KeyRound className="w-4 h-4 text-slate-500" />
                {isLogin ? 'MPIN (4 digits)' : 'Set MPIN (4 digits) *'}
              </Label>
              <MPINInput
                value={form.mpin}
                onChange={(val) => setForm({ ...form, mpin: val })}
                placeholder={isLogin ? "Enter your MPIN" : "Create your MPIN"}
              />
              {!isLogin && <p className="text-xs text-slate-500">This will be used to login to your account</p>}
            </div>

            {!isLogin && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      data-testid="vendor-phone-input"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="+91 98765 43210"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="gst">GST Number</Label>
                    <Input
                      id="gst"
                      data-testid="vendor-gst-input"
                      value={form.gst_number}
                      onChange={(e) => setForm({ ...form, gst_number: e.target.value })}
                      placeholder="27AABCU1234C1Z5"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="license">License Number</Label>
                  <Input
                    id="license"
                    data-testid="vendor-license-input"
                    value={form.license_number}
                    onChange={(e) => setForm({ ...form, license_number: e.target.value })}
                    placeholder="License/Registration number"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Business Address</Label>
                  <Textarea
                    id="address"
                    data-testid="vendor-address-input"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    placeholder="Full business address"
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Business Description</Label>
                  <Textarea
                    id="description"
                    data-testid="vendor-description-input"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Brief description of your services"
                    rows={2}
                  />
                </div>

                {/* Referral Code Field */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Gift className="w-4 h-4 text-amber-500" />
                    Referral Code (Optional)
                  </Label>
                  <div className="relative">
                    <Input 
                      data-testid="vendor-referral-input" 
                      placeholder="Enter doctor referral code" 
                      value={form.referral_code} 
                      onChange={e => {
                        const code = e.target.value.toUpperCase();
                        setForm({...form, referral_code: code});
                        validateReferralCode(code);
                      }} 
                      className={`pr-10 ${referralStatus === 'valid' ? 'border-green-500 focus:border-green-500' : referralStatus === 'invalid' ? 'border-red-400' : ''}`}
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {referralStatus === 'checking' && <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />}
                      {referralStatus === 'valid' && <CheckCircle className="w-5 h-5 text-green-500" />}
                      {referralStatus === 'invalid' && <XCircle className="w-5 h-5 text-red-400" />}
                    </div>
                  </div>
                  {referralStatus === 'valid' && referrerName && (
                    <p className="text-sm text-green-600">Referred by: {referrerName}</p>
                  )}
                  {referralStatus === 'invalid' && (
                    <p className="text-sm text-red-500">Invalid referral code</p>
                  )}
                </div>

                {/* MR/Sales Executive Section */}
                <div className="border-t pt-4 mt-4">
                  <button
                    type="button"
                    onClick={() => setShowMRFields(!showMRFields)}
                    className="flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700 font-medium mb-3"
                  >
                    <span>{showMRFields ? '−' : '+'}</span>
                    Add MR/Sales Executive Details (Optional)
                  </button>
                  
                  {showMRFields && (
                    <div className="space-y-3 bg-slate-50 p-4 rounded-lg">
                      <p className="text-xs text-slate-500 mb-2">Add your Medical Representative or Sales Executive contact for better coordination with clinics.</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">MR Name</Label>
                          <Input
                            data-testid="mr-name-input"
                            value={form.mr_executive.name}
                            onChange={(e) => setForm({ ...form, mr_executive: { ...form.mr_executive, name: e.target.value } })}
                            placeholder="Executive name"
                            className="h-10"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">MR Phone</Label>
                          <Input
                            data-testid="mr-phone-input"
                            value={form.mr_executive.phone}
                            onChange={(e) => setForm({ ...form, mr_executive: { ...form.mr_executive, phone: e.target.value } })}
                            placeholder="+91 98765 43210"
                            className="h-10"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">MR Email</Label>
                          <Input
                            data-testid="mr-email-input"
                            type="email"
                            value={form.mr_executive.email}
                            onChange={(e) => setForm({ ...form, mr_executive: { ...form.mr_executive, email: e.target.value } })}
                            placeholder="mr@company.com"
                            className="h-10"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Territory/Region</Label>
                          <Input
                            data-testid="mr-territory-input"
                            value={form.mr_executive.territory}
                            onChange={(e) => setForm({ ...form, mr_executive: { ...form.mr_executive, territory: e.target.value } })}
                            placeholder="e.g., North Delhi"
                            className="h-10"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            <Button
              type="submit"
              data-testid="vendor-submit-btn"
              className="w-full bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700"
              disabled={loading}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {isLogin ? 'Sign In' : 'Register Business'}
            </Button>
            
            {isLogin && (
              <div className="text-center mt-3">
                <button
                  type="button"
                  onClick={() => setShowForgotMPIN(true)}
                  className="text-sm text-teal-600 hover:text-teal-700 hover:underline"
                >
                  Forgot MPIN?
                </button>
              </div>
            )}
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setGoogleData(null);
              }}
              className="text-sm text-teal-600 hover:text-teal-700 font-medium"
            >
              {isLogin ? 'New vendor? Register your business' : 'Already registered? Sign in'}
            </button>
          </div>

          <div className="mt-4 pt-4 border-t text-center space-x-4">
            <a href="/" className="text-sm text-slate-500 hover:text-slate-700">Doctor Portal</a>
            <span className="text-slate-300">|</span>
            <a href="/super-admin" className="text-sm text-slate-500 hover:text-slate-700">Admin Portal</a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
